import { useRef } from 'react';
import type { KeyboardEvent, PointerEvent } from 'react';
import { resolveBadge } from '@cloudsign/shared';
import type { BadgeElement, BadgeLayout, DrawOp } from '@cloudsign/shared';
import { t } from '../../lib/i18n';
import { nudge, removeElement, snap, surfaceMm, updateElement } from './layout';

const DOT_MM = 25.4 / 300;

/** A short, readable name for an element ("Text Visitor name"). */
export function elementName(el: BadgeElement, fieldLabels: Record<string, string> = {}): string {
  switch (el.type) {
    case 'text': {
      const detail =
        el.source.kind === 'static'
          ? el.source.text
          : el.source.key.startsWith('field:')
            ? t('badgeUi.data.field', { label: fieldLabels[el.source.key] ?? t('badgeUi.data.fieldOther') })
            : t(`badgeUi.data.${el.source.key}`);
      return t('badgeUi.elementName', { kind: t('badgeUi.kind.text'), detail }).trim();
    }
    case 'image':
      return t('badgeUi.elementName', { kind: t('badgeUi.kind.image'), detail: t(`badgeUi.image.${el.source}`) });
    case 'qr':
      return t('badgeUi.kind.qr');
    case 'box':
      return t('badgeUi.kind.box');
  }
}

/** Lines of text broken to roughly fit a width, using an average glyph width. */
function wrap(text: string, charsPerLine: number, maxLines: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    if (cur && (cur + ' ' + w).length > charsPerLine) {
      lines.push(cur);
      cur = w;
    } else {
      cur = cur ? `${cur} ${w}` : w;
    }
  }
  if (cur) lines.push(cur);
  return lines.slice(0, maxLines);
}

function TextOp({ op }: { op: Extract<DrawOp, { kind: 'text' }> }) {
  const glyph = 0.55;
  let size = op.fontSizeDots;
  let lines = wrap(op.text, Math.max(1, Math.floor(op.w / (size * glyph))), op.maxLines);
  if (op.shrinkToFit) {
    const longest = Math.max(1, ...lines.map((l) => l.length));
    size = Math.min(size, op.w / (longest * glyph), op.h / (lines.length * 1.15));
    lines = wrap(op.text, Math.max(1, Math.floor(op.w / (size * glyph))), op.maxLines);
  }
  const anchor = op.align === 'left' ? 'start' : op.align === 'center' ? 'middle' : 'end';
  const x = op.align === 'left' ? op.x : op.align === 'center' ? op.x + op.w / 2 : op.x + op.w;
  return (
    <text x={x} y={op.y} fontSize={size} fontWeight={op.bold ? 700 : 400} textAnchor={anchor} fontFamily="Helvetica, Arial, sans-serif" fill="#000">
      {lines.map((line, i) => (
        <tspan key={i} x={x} dy={i === 0 ? size * 0.95 : size * 1.15}>
          {line}
        </tspan>
      ))}
    </text>
  );
}

function Op({ op }: { op: DrawOp }) {
  switch (op.kind) {
    case 'text':
      return <TextOp op={op} />;
    case 'image':
      return (
        <g>
          <rect x={op.x} y={op.y} width={op.w} height={op.h} fill="#e2e8f0" stroke="#94a3b8" strokeWidth={2} />
          <line x1={op.x} y1={op.y} x2={op.x + op.w} y2={op.y + op.h} stroke="#94a3b8" strokeWidth={2} />
          <line x1={op.x + op.w} y1={op.y} x2={op.x} y2={op.y + op.h} stroke="#94a3b8" strokeWidth={2} />
        </g>
      );
    case 'qr': {
      // A stand-in pattern: a real code needs the visit. Three finder squares and a few modules.
      const m = op.size / 7;
      const finder = (cx: number, cy: number) => <rect key={`${cx}-${cy}`} x={op.x + cx * m} y={op.y + cy * m} width={2 * m} height={2 * m} fill="#000" />;
      return (
        <g>
          <rect x={op.x} y={op.y} width={op.size} height={op.size} fill="#fff" stroke="#000" strokeWidth={2} />
          {finder(0.5, 0.5)}
          {finder(4.5, 0.5)}
          {finder(0.5, 4.5)}
          <rect x={op.x + 3.5 * m} y={op.y + 3.5 * m} width={m} height={m} fill="#000" />
          <rect x={op.x + 5 * m} y={op.y + 5 * m} width={m} height={m} fill="#000" />
        </g>
      );
    }
    case 'box':
      return (
        <rect
          x={op.x + (op.fill ? 0 : op.strokeDots / 2)}
          y={op.y + (op.fill ? 0 : op.strokeDots / 2)}
          width={Math.max(1, op.w - (op.fill ? 0 : op.strokeDots))}
          height={Math.max(1, op.h - (op.fill ? 0 : op.strokeDots))}
          fill={op.fill ? '#000' : 'none'}
          stroke="#000"
          strokeWidth={op.strokeDots}
        />
      );
  }
}

interface DragState {
  mode: 'move' | 'resize';
  id: string;
  startX: number;
  startY: number;
  orig: { x: number; y: number; w: number; h: number };
}

/**
 * The label at true proportions (1 SVG unit = 1 mm). The picture comes from the shared `resolveBadge`, so it
 * matches what prints; transparent frames on top carry the selection, dragging and keyboard moves.
 */
export function LabelCanvas({
  layout,
  values,
  selectedId,
  onSelect,
  onChange,
  canEdit,
  outside,
  fieldLabels,
}: {
  layout: BadgeLayout;
  values: Record<string, string>;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onChange: (next: BadgeLayout) => void;
  canEdit: boolean;
  outside: ReadonlySet<string>;
  fieldLabels: Record<string, string>;
}) {
  const { width, height } = surfaceMm(layout);
  const svgRef = useRef<SVGSVGElement>(null);
  const drag = useRef<DragState | null>(null);
  const resolved = resolveBadge(layout, {
    values,
    hasPhoto: true,
    logos: { tenant: true, site: true, template: true },
    qrPayload: 'CS1.V.sample',
  });

  const mmPerPixel = (): number => {
    const w = svgRef.current?.getBoundingClientRect().width ?? 0;
    return w > 0 ? width / w : 0;
  };

  const begin = (e: PointerEvent<SVGElement>, el: BadgeElement, mode: DragState['mode']) => {
    e.stopPropagation();
    onSelect(el.id);
    if (!canEdit) return;
    drag.current = { mode, id: el.id, startX: e.clientX, startY: e.clientY, orig: { x: el.x, y: el.y, w: el.w, h: el.h } };
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
  };

  const move = (e: PointerEvent<SVGElement>) => {
    const d = drag.current;
    const k = mmPerPixel();
    if (!d || k === 0) return;
    const dx = (e.clientX - d.startX) * k;
    const dy = (e.clientY - d.startY) * k;
    onChange(
      d.mode === 'move'
        ? updateElement(layout, d.id, { x: snap(d.orig.x + dx), y: snap(d.orig.y + dy) })
        : updateElement(layout, d.id, { w: snap(d.orig.w + dx), h: snap(d.orig.h + dy) }),
    );
  };

  const end = () => {
    drag.current = null;
  };

  const key = (e: KeyboardEvent<SVGElement>, el: BadgeElement) => {
    if (!canEdit) return;
    const arrows: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    const step = arrows[e.key];
    if (step) {
      e.preventDefault();
      onChange(nudge(layout, el.id, step[0], step[1], e.shiftKey));
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      onChange(removeElement(layout, el.id));
      onSelect(null);
    }
  };

  return (
    <svg
      ref={svgRef}
      role="group"
      aria-label={t('badgeUi.designer.canvas', { width, height })}
      viewBox={`0 0 ${width} ${height}`}
      className="w-full max-w-full touch-none rounded border border-slate-300 bg-white shadow-sm"
      style={{ aspectRatio: `${width} / ${height}` }}
      onPointerDown={() => onSelect(null)}
    >
      <rect x={0} y={0} width={width} height={height} fill="#fff" />
      <g transform={`scale(${DOT_MM})`} pointerEvents="none">
        {resolved.ops.map((op, i) => (
          <Op key={i} op={op} />
        ))}
      </g>
      {layout.elements.map((el) => {
        const selected = el.id === selectedId;
        const bad = outside.has(el.id);
        const stroke = bad ? '#b91c1c' : selected ? 'var(--brand-primary, #2563eb)' : '#64748b';
        return (
          <g key={el.id}>
            <rect
              x={el.x}
              y={el.y}
              width={el.w}
              height={el.h}
              fill="transparent"
              stroke={stroke}
              strokeWidth={selected ? 0.5 : 0.25}
              strokeDasharray={selected ? undefined : '1 1'}
              role="button"
              tabIndex={0}
              aria-pressed={selected}
              aria-label={elementName(el, fieldLabels)}
              className={canEdit ? 'cursor-move focus:outline-none' : 'cursor-pointer focus:outline-none'}
              onFocus={() => onSelect(el.id)}
              onPointerDown={(e) => begin(e, el, 'move')}
              onPointerMove={move}
              onPointerUp={end}
              onPointerCancel={end}
              onKeyDown={(e) => key(e, el)}
            />
            {selected && canEdit ? (
              <rect
                x={el.x + el.w - 1.5}
                y={el.y + el.h - 1.5}
                width={3}
                height={3}
                fill={stroke}
                aria-hidden="true"
                className="cursor-nwse-resize"
                onPointerDown={(e) => begin(e, el, 'resize')}
                onPointerMove={move}
                onPointerUp={end}
                onPointerCancel={end}
              />
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}
